import { Injectable } from '@angular/core';
import { Events, AlertController } from 'ionic-angular';
import Amplify, { Auth, API } from 'aws-amplify';
import { aws_exports } from './aws-exports';
import { errorMap } from './errorMap';

@Injectable()
export class AwsProvider {

  successfulLambda: boolean;
  defaultErrorMessage: string;

  constructor(public events: Events, private alertCtrl: AlertController) {
    Amplify.configure(aws_exports);
    this.defaultErrorMessage = 'Something went wrong. Please try again.';
  }

  /**
   * Convert the returned AWS error into a string we can show
   * the user, if applicable
   * @param functionName what function was run
   * @param error error object from AWS
   */
  getErrorMessage(functionName: string, error: object) {
    if (error['code'] && errorMap[functionName] && errorMap[functionName]['code'][error['code']]) {
      let options: object = errorMap[functionName]['code'][error['code']];
      if (typeof options == 'object' && error['message']) {
        if (options['messageContains']) {
          let substrings: Array<string> = Object.keys(options['messageContains']);
          for (let substring of substrings) {
            if (error['message'].includes(substring)) {
              return options['messageContains'][substring];
            }
          }
          return 'An unknown error occured.';
        }
      } else { // default string error for code
        return options;
      }
    } else if (error['message']) {
      return error['message'];
    } else {
      return 'An unknown error occured.'
    }
  }
  
	/**
   * Sign in to SherloQ Admin using a username and password
   * @param username the user's username
   * @param password password for username
   */
  signIn(username: string, password: string): Promise<object> {
    return Auth.signIn(username, password)
      .then((response) => {
        console.log("signing in. Response:");
        console.log(response);
        if (response['challengeName'] == 'NEW_PASSWORD_REQUIRED') {
          return Auth.completeNewPassword(response, password, {})
            .then((response) => {
              return { success: true };
            }).catch((error) => {
              console.log(JSON.stringify(error));
              return { success: false, message: 'Unknown error.' };
            });
        } else {
          return { success: true };
        }
      }).catch((error) => {
        console.log(JSON.stringify(error));
        if (error['message']) {
          if (error['code'] == 'UnexpectedLambdaException') {
            if (!password || password.length == 0) {
              return { success: false, message: 'Password cannot be empty' };
            } else {
              return { success: false, message: 'Unknown error.' };
            }
          } else {
            return { success: false, message: error['message'] };
          }
        } else {
          if (typeof error == 'string') {
            return { success: false, message: error };
          } else {
            return { success: false, message: 'Unknown error.' };
          }
        }
      });
  }

  /**
   * Deletes a users local credentials for SherloQ
   */
  signOut(): Promise<void> {
    window.localStorage.removeItem('userCache');
    window.localStorage.removeItem('allUserCache');
    return Auth.signOut()
      .catch((error) => {
        console.log(JSON.stringify(error));
      });
  }

  /**
   * Checks if the user is logged in and has credentials locally
   */
  userLoggedIn(): Promise<boolean> {
    console.log('Checking if user is logged in');
    return Auth.currentAuthenticatedUser()
      .then((user) => {
        console.log('User is logged in');
        return true;
      })
      .catch((error) => {
        console.log('User is not logged in');
        return false;
      });
  }

  /**
   * If logged in and a Lambda fails due to invalid or no credentials,
   * show a message that will log the user out in order to 
   * re-authenticate and get new credentials
   */
  invalidCredentialLogout() {
    let alert = this.alertCtrl.create({
      message: 'Your credentials have expired. Please log in to your account again.',
      buttons: [
        {
          text: 'Ok',
          handler: () => {
            this.events.publish('sideMenu:logoutConfirmed');
          }
        }
      ],
      enableBackdropDismiss: false,
      cssClass: 'standard-alert'
    });
    alert.present();
  }

  /**
   * Make an AWS API Gateway post request
   * @param endpoint name of API endpoint to use
   * @param data (optional) JSON body of request to pass
   */
  makeApiCall(whichAPI: string, endpoint: string, data?: object): Promise<object> {
    if (!data) data = {};
    return Auth.currentAuthenticatedUser()
      .then((user) => {
        const token = user.signInUserSession.idToken.jwtToken;
        const request = {
          body: data,
          headers: { Authorization: token }
        };
        return API.post(whichAPI, endpoint, request)
          .then((response) => {
            return { success: true, response: response };
          })
          .catch((error) => {
            return { success: false, error: error };
          });
      })
      .catch((error) => {
        console.log('Error getting current authenticated user:');
        console.log(JSON.stringify(error));
        return { success: false, error: error };
      });
  }

  /**
   * Call a SherloQ Lambda and return the response
   * @param functionName name of Lambda function to call
   * @param payload object to send to Lambda as data payload
   */
  async invokeLambda(functionName: string, payload?: object): Promise<any> {
    console.log('Invoking lambda ' + functionName);
    return Auth.currentUserCredentials()
      .then((credentials) => {
        if (!payload) payload = {};
        if (credentials['expired']) {
          console.log('Token expired');
          this.invalidCredentialLogout();
        }
        var jwtToken: string;
        try {
          // cookie key for this user pool
          const key: string = 'cognito-idp.' + aws_exports['Auth']['region'] +
            '.amazonaws.com/' + aws_exports['Auth']['userPoolId'];
          jwtToken = credentials['params']['Logins'][key];
          if (jwtToken == null) {
            console.log('Count not get JWT token for user');
            this.invalidCredentialLogout();
          }
        } catch (error) {
          console.log('Count not get JWT token for user');
          this.invalidCredentialLogout();
        }
        payload['token'] = jwtToken;
        // console.log('Appending JWT to payload: ' + payload['token']);
        return API.post('SherloQLambdaAPI', functionName + '/invocations', { body: payload })
          .then((response) => {
            console.log(functionName + ' successful');
            // console.log(JSON.stringify(response));
            if (response && response['errorMessage']) {
              return { success: false, response: response['errorMessage'] };
            } else {
              this.successfulLambda = true;
              return { success: true, response: response };
            }
          })
          .catch((error) => {
            console.log(functionName + ' error');
            console.log(JSON.stringify(error));
            return { success: false, response: this.defaultErrorMessage  };
          });
      })
      .catch((error) => {
        console.log(functionName + ' fail - could not get user credentials');
        console.log(JSON.stringify(error));
        this.invalidCredentialLogout();
        return { success: false, response: this.defaultErrorMessage };
      })
  }
  
  /** 
   * Returns true if we are connected to the live AWS account, 
   * Returns false if we are connected to the dev AWS account.
   */
  isLive(): boolean {
    if(aws_exports['Auth']['userPoolId'] == "us-east-1_Tp75o9fj3"){
      return true;
    }else{
      return false;
    }
  }
}




// WEBPACK FOOTER //
// ./src/providers/aws/aws.ts